import { Blattart, blattform } from './blattformen';
import { AuflageStreifen } from './grubenbild-abschnitte';
import { HORIZONT_FARBEN } from './horizont-farben';
import { el, streu } from './svg-werkzeug';

/**
 * Die Humusauflage vergroessert: ein kleines Bild von Ol, Of und Oh.
 *
 * Nutzer-Wunsch 21.09.2026: Ein Klick auf die Beschriftung der Auflage oeffnet
 * einen vergroesserten Ausschnitt. Im Grubenbild selbst sind die drei Lagen
 * meist nur wenige Pixel hoch; was sie unterscheidet, sieht man dort nicht.
 *
 * Wie die Lagen aussehen, folgt den ueblichen Schemata der Humusformen und
 * der SEA 95 (Teil B, S. 66 bis 69):
 *  - Ol: locker liegende, ganze Blaetter von Ahorn, Linde, Ulme und Aspe,
 *    Kiefernnadeln, ein Zweigstueck (Formen in `blattformen.ts`),
 *  - Of: zerkleinerte, teils gebleichte Blattreste, weisse Pilzfaeden,
 *    Feinwurzeln und Kotkruemel von Bodentieren,
 *  - Oh: schwaerzlich-braune, kruemelige Feinsubstanz, durchwurzelt.
 *
 * **Die Maechtigkeiten stehen im richtigen Verhaeltnis zueinander**
 * (Nutzer-Vorgabe 21.09.2026: „die O-Lagen bitte ungefaehr maszstaeblich,
 * wenigstens die Relationen der Maechtigkeiten wahren"). Eine duenne Lage
 * wird NICHT aufgezogen; passt ihre Beschriftung nicht daneben, weicht die
 * Beschriftung aus und zeigt mit einer Linie auf ihre Lage. Blaetter, Faeden
 * und Kruemel sind dagegen schematisch und nicht maszstaeblich.
 *
 * **Dieselben Texturen zeichnet seit dem 25.09.2026 auch das Grubenbild** in
 * seine Humusauflage, verkleinert (Nutzer-Wunsch: „die O-Auflagen sollen im
 * Profil die gleichen Texturen verwenden wie in der Auflagen-Vorschau, nur
 * kleinskaliert"). Das Bild ohne Beschriftung steht in `zeichneAuflageBild`;
 * fuers Grubenbild legt `legeAuflageAusKacheln` dieselben Bausteine
 * verkleinert aus Kacheln - leicht und ohne erkennbare Wiederholung.
 *
 * Reine Funktion ohne Angular, damit sie ohne TestBed pruefbar ist.
 */

type Feld = AuflageStreifen['feld'];

/**
 * Bezeichnung und Erlaeuterung je Lage, woertlich nach SEA 95, Teil B,
 * Darst. B 3-12 (gleichlautend Teil A, S. 41).
 */
export const LUPEN_TEXT: Readonly<Record<Feld, { name: string; erlaeuterung: string }>> = {
	Ol: {
		name: 'Förna-Subhorizont',
		erlaeuterung: 'überwiegend äußerlich unveränderte Streu- und Vegetationsabfälle',
	},
	Of: {
		name: 'Vermoderungs-Subhorizont',
		erlaeuterung: 'überwiegend angerottete Abfälle mit noch erkennbarer Struktur',
	},
	Oh: {
		name: 'Feinhumus-Subhorizont',
		erlaeuterung: 'überwiegend zersetzte Pflanzenabfälle ohne erkennbare Struktur',
	},
};

/** Breite des Bildes und der Beschriftung daneben, in Pixel. */
export const LUPE_BILD_BREITE = 150;
const TEXT_ABSTAND = 16;
const TEXT_BREITE = 180;
const RAND = 4;
/** So viele Zeichen passen in eine Zeile der Erlaeuterung (Schrift 10,5). */
const ZEICHEN_JE_ZEILE = 31;
const ZEILE = 13;
/** Luft zwischen zwei Beschriftungen, die einander ausweichen. */
const TEXT_LUECKE = 8;

/** So hoch werden alle Lagen zusammen, wenn nichts dagegen spricht. */
export const LUPE_HOEHE = 190;
/**
 * So hoch soll die duennste Lage mindestens werden - dafuer darf das ganze
 * Bild hoeher werden, aber nicht ueber `LUPE_HOEHE_HOECHSTENS`. Das
 * Verhaeltnis der Lagen bleibt in jedem Fall erhalten.
 */
export const LAGE_DUENNSTE = 16;
export const LUPE_HOEHE_HOECHSTENS = 300;

export interface LupenLage {
	feld: Feld;
	maechtigkeit: number;
	/** Ober- und Unterkante im Bild, in Pixel ab der Oberkante. */
	von: number;
	bis: number;
}

/** Bricht einen Text an Wortgrenzen um. */
export function umbrechen(text: string, zeichen = ZEICHEN_JE_ZEILE): string[] {
	const zeilen: string[] = [];
	let zeile = '';
	for (const wort of text.split(/\s+/).filter(Boolean)) {
		const neu = zeile ? `${zeile} ${wort}` : wort;
		if (neu.length > zeichen && zeile) {
			zeilen.push(zeile);
			zeile = wort;
		} else {
			zeile = neu;
		}
	}
	if (zeile) zeilen.push(zeile);
	return zeilen;
}

/**
 * Die Lagen von oben nach unten (Ol, Of, Oh), mit ihrer Hoehe im Bild.
 *
 * Alle mit DEMSELBEN Maszstab: Die Hoehen stehen zueinander wie die
 * Maechtigkeiten. `auflageStreifen` liefert die Lagen von unten nach oben;
 * eine Lage ohne Maechtigkeit steht dort gar nicht erst drin.
 */
export function lupenLagen(zeilen: AuflageStreifen[]): LupenLage[] {
	const gesamt = zeilen.reduce((s, z) => s + z.maechtigkeit, 0);
	if (!(gesamt > 0)) return [];
	const duennste = Math.min(...zeilen.map(z => z.maechtigkeit));
	let pxJeCm = LUPE_HOEHE / gesamt;
	if (pxJeCm * duennste < LAGE_DUENNSTE) pxJeCm = LAGE_DUENNSTE / duennste;
	pxJeCm = Math.min(pxJeCm, LUPE_HOEHE_HOECHSTENS / gesamt);
	let y = 0;
	return [...zeilen].reverse().map(z => {
		const lage = { feld: z.feld, maechtigkeit: z.maechtigkeit, von: y, bis: y + z.maechtigkeit * pxJeCm };
		y = lage.bis;
		return lage;
	});
}

export interface LupenText {
	lage: LupenLage;
	/** Oberkante der Beschriftung, in Pixel ab der Oberkante des Bildes. */
	oben: number;
	hoehe: number;
}

/**
 * Wo die Beschriftungen stehen.
 *
 * Jede moechte auf der Mitte ihrer Lage stehen. Ist die Lage duenner als die
 * Beschriftung, ruecken die Beschriftungen auseinander - erst nach unten,
 * dann, wenn sie unten ueber das Bild hinausliefen, wieder nach oben, soweit
 * Platz ist. Die Lagen selbst bleiben, wie sie sind.
 */
export function lupenTexte(lagen: LupenLage[]): LupenText[] {
	const texte = lagen.map(lage => {
		const hoehe = (2 + umbrechen(LUPEN_TEXT[lage.feld].erlaeuterung).length) * ZEILE;
		return { lage, oben: (lage.von + lage.bis) / 2 - hoehe / 2, hoehe };
	});
	const bild = lagen.length ? lagen[lagen.length - 1].bis : 0;
	let frei = 0;
	for (const t of texte) {
		t.oben = Math.max(t.oben, frei);
		frei = t.oben + t.hoehe + TEXT_LUECKE;
	}
	const noetig = texte.reduce((s, t) => s + t.hoehe, 0) + TEXT_LUECKE * Math.max(0, texte.length - 1);
	let grenze = Math.max(bild, noetig);
	for (let i = texte.length - 1; i >= 0; i--) {
		texte[i].oben = Math.min(texte[i].oben, grenze - texte[i].hoehe);
		grenze = texte[i].oben - TEXT_LUECKE;
	}
	return texte;
}

/** So breit wird das Fenster mit der Lupe hoechstens, in Pixel. */
export const LUPE_FENSTER_BREITE = 390;

/**
 * Wo das Fenster mit der Lupe steht, gemessen im umgebenden Feld.
 *
 * Unter dem Klick, waagerecht auf ihn zentriert - aber nie ueber den Rand des
 * Feldes hinaus: Der Klick liegt am rechten Bildrand, dort steht die
 * Beschriftung der Auflage, und ein zentriertes Fenster ragte sonst rechts
 * hinaus. Auf einem schmalen Schirm wird es so breit wie das Feld.
 */
export function lupenOrt(
	klickX: number, klickY: number,
	feld: { left: number; top: number; width: number },
): { x: number; y: number; breite: number } {
	const breite = Math.min(LUPE_FENSTER_BREITE, feld.width);
	const x = Math.max(0, Math.min(feld.width - breite, klickX - feld.left - breite / 2));
	const y = Math.max(0, klickY - feld.top + 14);
	return { x, y, breite };
}

/** Zeichnet das Bild. Ohne Auflage entsteht ein leeres SVG. */
export function zeichneAuflageLupe(zeilen: AuflageStreifen[]): SVGElement {
	const lagen = lupenLagen(zeilen);
	const texte = lupenTexte(lagen);
	const bildHoehe = lagen.length ? lagen[lagen.length - 1].bis : 0;
	const textHoehe = texte.reduce((m, t) => Math.max(m, t.oben + t.hoehe), 0);
	// Ueber dem Bild so viel Platz, dass kein Blatt der Streu oben
	// abgeschnitten wird (Nutzer-Vorgabe 21.09.2026).
	const oben = RAND + streuUeberstand(lagen);
	const hoehe = oben + Math.max(bildHoehe, textHoehe) + RAND;
	const breite = RAND + LUPE_BILD_BREITE + TEXT_ABSTAND + TEXT_BREITE;
	const svg = el('svg', {
		width: breite, height: hoehe, viewBox: `0 0 ${breite} ${hoehe}`,
		class: 'auflage-lupe-svg', role: 'img', 'aria-label': 'Humusauflage, vergrößert',
	});
	const defs = el('defs');
	svg.appendChild(defs);
	zeichneAuflageBild(svg, defs, lagen, RAND, oben, LUPE_BILD_BREITE, hoehe);
	texte.forEach(t => beschrifte(svg, t, oben));
	return svg;
}

/**
 * Wie viel Platz die Streu ueber der obersten Lage braucht, in Pixel - und
 * einen mehr, damit auch die Kante des obersten Blattes noch drin ist.
 */
function streuUeberstand(lagen: LupenLage[]): number {
	const ol = lagen.find(l => l.feld === 'Ol');
	return ol ? streuUeberstandOben(ol.bis - ol.von) + 1 : 0;
}

/**
 * Das Bild der Lupe ohne Beschriftung: `oben` ist die Oberkante der obersten
 * Lage, `x` und `b` linker Rand und Breite, `hoehe` der untere Bildrand (bis
 * dahin darf die Streu reichen) - alles in Lupen-Pixeln. Jede Lage wird links
 * und rechts an der Bildkante abgeschnitten.
 *
 * Dieselben Bausteine legt `legeAuflageAusKacheln` verkleinert ins Grubenbild.
 */
function zeichneAuflageBild(
	ziel: SVGElement, defs: SVGElement, lagen: LupenLage[],
	x: number, oben: number, b: number, hoehe: number,
): void {
	// Die Streu zuletzt: Ihre Blaetter liegen auf dem Of und duerfen ueber
	// die Grenze hinweg aufliegen (Nutzer-Vorgabe 21.09.2026) - abgeschnitten
	// wird sie nur links und rechts am Bildrand. Einen Grund hat sie nicht:
	// Die Blaetter fuellen die Lage, dazwischen bleibt es durchsichtig.
	const reihenfolge = [...lagen.filter(l => l.feld !== 'Ol'), ...lagen.filter(l => l.feld === 'Ol')];
	const ol = lagen.find(l => l.feld === 'Ol');
	const of = lagen.find(l => l.feld === 'Of'), oh = lagen.find(l => l.feld === 'Oh');
	reihenfolge.forEach(l => {
		// Vor der Streu (oder nach Oh, wenn es keine Streu gibt) der weiche
		// Uebergang zwischen Of und Oh - er liegt ueber beiden.
		if (l.feld === 'Ol') {
			if (of && oh) ziel.appendChild(uebergang(defs, x, oben, b, of, oh, 'lupe'));
			// Die Lage unter der Streu laeuft nach oben in deren Luecken aus.
			const darunter = lagen[lagen.indexOf(l) + 1];
			if (darunter) ziel.appendChild(auslaufen(defs, x, oben, b, darunter, l, 'lupe'));
		}
		// Die Streu wird nur links und rechts abgeschnitten: Oben ragt kein
		// Blatt hinaus, unten liegen die untersten auf dem Of auf - eine
		// gerade Schnittkante sah dort hart aus (Nutzer-Befund 21.09.2026).
		ziel.appendChild(zeichneLage(defs, l, x, oben, b, {
			idPraefix: 'lupe', variante: 0, mitGrund: true, bibliothek: null, dichte: 1,
			ausschnitt: l.feld === 'Ol'
				? { x, y: 0, width: b, height: hoehe }
				: { x, y: oben + l.von, width: b, height: l.bis - l.von },
		}));
	});
	if (of && oh && !ol) ziel.appendChild(uebergang(defs, x, oben, b, of, oh, 'lupe'));
}

interface LagenOptionen {
	/** Steht vor jeder id (Ausschnitt) und muss je SVG einmalig sein. */
	idPraefix: string;
	/**
	 * Welcher Zufall: 0 ist das Bild der Lupe, jede weitere Nummer ein anderes
	 * Blatt- und Kruemelbild nach denselben Regeln (fuer die Kacheln des
	 * Grubenbildes).
	 */
	variante: number;
	/**
	 * Den Grund der Lage mitzeichnen (Of und Oh; die Streu hat keinen). In den
	 * Kacheln liegt der Grund stattdessen einmal ueber die ganze Breite.
	 */
	mitGrund: boolean;
	/** Der Ausschnitt der Lage; ohne bleibt sie unbeschnitten. */
	ausschnitt: { x: number; y: number; width: number; height: number } | null;
	/**
	 * Kacheln des Grubenbildes: Die Blattformen liegen einmal in `defs`, jedes
	 * Blatt ist ein Verweis darauf. Ohne (Lupe) wird jedes Blatt gezeichnet.
	 */
	bibliothek: BlattBibliothek | null;
	/** Anteil der Kruemel gegenueber der Lupe, siehe `kruemelDichte`; 1 in der Lupe. */
	dichte: number;
}

/** Fuer jede weitere Variante ein anderer Startwert des Zufalls. */
const VARIANTEN_SCHRITT = 104729;

/** Eine Lage: ihr Ausschnitt, bei Of und Oh der Grund, darauf ihr Zeug. */
function zeichneLage(defs: SVGElement, l: LupenLage, x: number, oben: number, b: number, o: LagenOptionen): SVGElement {
	const y = oben + l.von, h = l.bis - l.von;
	const g = el('g', { class: `lupe-lage lupe-${l.feld}` });
	if (o.ausschnitt) {
		const clipId = `${o.idPraefix}-${l.feld}`;
		defs.appendChild(el('clipPath', { id: clipId }, [el('rect', o.ausschnitt)]));
		g.setAttribute('clip-path', `url(#${clipId})`);
	}
	const r = streu(l.feld.charCodeAt(1) * 7919 + 101 + o.variante * VARIANTEN_SCHRITT);
	if (l.feld === 'Ol') {
		// Die Streu der Lupe; im Grubenbild zeichnet `streuInReihen` sie.
		foerna(g, x, y, b, h, r);
	} else {
		if (o.mitGrund) g.appendChild(grund(l, x, oben, b));
		if (l.feld === 'Of') vermoderung(g, x, y, b, h, r, o.bibliothek, o.dichte);
		else feinhumus(g, x, y, b, h, r, o.dichte);
	}
	return g;
}

/** Die Grundfarbe einer Lage (Of, Oh) ueber die Breite `b`. */
function grund(l: LupenLage, x: number, oben: number, b: number): SVGElement {
	return el('rect', { x, y: oben + l.von, width: b, height: l.bis - l.von, fill: HORIZONT_FARBEN[l.feld].farbe });
}

/* --- Die Humusauflage im Grubenbild --------------------------------------- */

/** Die id der Auflage im Grubenbild; ihre Teile haengen daran (`gb-auflage-unten`, `gb-auflage-reihe3` …). */
export const KACHEL_ID = 'gb-auflage';

/**
 * So viele verschiedene Blaetter zeichnet die Streu des Grubenbildes
 * hoechstens, nach der Groesse eines Blattes auf dem Schirm (in Pixel).
 *
 * Jedes gezeichnete Blatt kostet ein Element; was das Bild darueber hinaus
 * fuellt, sind Wiederholungen je Reihe (`streuInReihen`). Je groesser die
 * Blaetter, desto eher faellt eine Wiederholung auf - deshalb wachsen die
 * verschiedenen Blaetter mit der Blattgroesse: 150 bei Flecken unter
 * anderthalb Pixeln, 800 ab acht Pixeln (dann reicht die Kachel einer Reihe
 * meist ueber die ganze Wand, und nichts wiederholt sich).
 */
export function streuBlattBudget(blattPx: number): number {
	return Math.max(150, Math.min(800, Math.round(100 * blattPx)));
}

/**
 * Wie viele Kruemel die Kacheln bekommen, als Anteil der Lupe.
 *
 * Ein Kruemel ist in der Lupe ein bis drei Pixel gross. Auf ein Zehntel
 * verkleinert waere er unsichtbar - und tausend unsichtbare Kruemel kosten
 * trotzdem (Nutzer 25.09.2026: „das soll eigentlich keine Ressourcen
 * fressen"). Deshalb weniger Kruemel, dafuer groessere: Die bedeckte Flaeche
 * bleibt gleich (Zahl mal Flaeche), das Bild bleibt gesprenkelt. Ab halber
 * Lupengroesse alle.
 */
export function kruemelDichte(faktor: number): number {
	return Math.min(1, Math.max(0.02, (faktor / 0.5) ** 2));
}

/**
 * Die Humusauflage fuers Grubenbild: die Bausteine der Lupe, verkleinert
 * (Nutzer-Wunsch 25.09.2026: „die gleichen Texturen wie in der
 * Auflagen-Vorschau, nur kleinskaliert").
 *
 * Gezeichnet wird in Lupen-Pixeln in eine Gruppe, die mit `faktor` auf den
 * Maszstab des Bildes verkleinert ist: Jede Lage wird genau `Maechtigkeit × px`
 * hoch (moeglich, weil `lupenLagen` alle Lagen mit DEMSELBEN Maszstab setzt),
 * derselbe Faktor in beide Richtungen - nichts wird verzerrt.
 *
 * **Leicht.** Of und Oh liegen als EINE Kachel in einem `<pattern>` (dreimal
 * um eine Kachelbreite versetzt, damit an der Naht nichts abgeschnitten ist),
 * mit `kruemelDichte` weniger Kruemeln. Ihre Wiederkehr faellt nicht auf, es
 * ist Rauschen. Der Grund der Lagen und die beiden Farbverlaeufe liegen einmal
 * ueber die ganze Breite - aneinandergesetzte Flaechen zeigten Haarrisse.
 *
 * **Die Streu zufaellig statt periodisch** - Reihe fuer Reihe mit eigenem
 * Zufall und eigener Verschiebung, siehe `streuInReihen`. Der Zufall haengt
 * am Punkt (`r`), dasselbe Profil sieht also immer gleich aus.
 *
 * Reihenfolge wie in der Lupe: Grund, Kruemel und Reste von Of und Oh, der
 * Verlauf zwischen ihnen, das Auslaufen unter der Streu, zuletzt die Streu.
 *
 * Zurueck kommt die Gruppe; ohne Lagen `null`.
 */
export function legeAuflageAusKacheln(
	defs: SVGElement, zeilen: AuflageStreifen[],
	x: number, unten: number, breite: number, px: number, r: () => number,
): SVGElement | null {
	const lagen = lupenLagen(zeilen);
	if (!lagen.length) return null;
	const bildHoehe = lagen[lagen.length - 1].bis;
	const gesamtCm = zeilen.reduce((s, z) => s + z.maechtigkeit, 0);
	// Lupen-Pixel zu Bild-Pixel: Die ganze Auflage ist im Bild `gesamtCm * px` hoch.
	const faktor = (gesamtCm * px) / bildHoehe;
	const ueber = streuUeberstand(lagen);
	const p = KACHEL_ID;
	const kachelB = LUPE_BILD_BREITE;
	/** Die Wandbreite in Lupen-Pixeln. */
	const b = breite / faktor;
	const dichte = kruemelDichte(faktor);
	const bibliothek = new BlattBibliothek(defs, p);
	const ol = lagen.find(l => l.feld === 'Ol');
	const of = lagen.find(l => l.feld === 'Of'), oh = lagen.find(l => l.feld === 'Oh');
	const unterStreu = ol ? lagen[lagen.indexOf(ol) + 1] : undefined;
	const untere = lagen.filter(l => l.feld !== 'Ol');

	const g = el('g', {
		class: 'gb-auflage-bild',
		transform: `translate(${f3(x)} ${f3(unten - bildHoehe * faktor)}) scale(${faktor.toFixed(4)})`,
	});
	// Beschnitten an den Wandkanten - in Lupen-Pixeln, denn der Ausschnitt
	// gilt in der verkleinerten Gruppe. Nach oben so viel Luft, wie die Streu
	// hinausragt.
	defs.appendChild(el('clipPath', { id: `${p}-wand` }, [
		el('rect', { x: 0, y: -ueber, width: f(b), height: ueber + bildHoehe }),
	]));
	g.setAttribute('clip-path', `url(#${p}-wand)`);

	// Der Grund, einmal ueber die ganze Breite.
	untere.forEach(l => g.appendChild(grund(l, 0, 0, b)));

	const optionen = (l: LupenLage, k: number, kb: number): LagenOptionen => ({
		idPraefix: `${p}-v${k}`, variante: k, mitGrund: false, bibliothek, dichte,
		// Nur oben und unten beschnitten, nie an den Seiten - und die Streu
		// gar nicht, sie darf ueber- und unterstehen.
		ausschnitt: l.feld === 'Ol'
			? null
			: { x: -kb, y: l.von, width: 3 * kb, height: l.bis - l.von },
	});
	// Eine Kachel als Muster ueber die ganze Breite: die Gruppe in defs, das
	// Muster legt sie dreimal versetzt, die Flaeche traegt es.
	const muster = (teil: string, kb: number, von: number, bis: number, fuellen: (ziel: SVGElement) => void): SVGElement => {
		const id = `${p}-${teil}`;
		const ziel = el('g', { id });
		fuellen(ziel);
		defs.appendChild(ziel);
		defs.appendChild(el('pattern', {
			id: `${id}-muster`, patternUnits: 'userSpaceOnUse', x: 0, y: von, width: kb, height: bis - von,
		}, [-kb, 0, kb].map(dx => el('use', { href: `#${id}`, x: dx }))));
		return el('rect', { x: 0, y: von, width: f(b), height: bis - von, fill: `url(#${id}-muster)` });
	};

	if (untere.length) {
		g.appendChild(muster('unten', kachelB, 0, bildHoehe, ziel => {
			untere.forEach(l => ziel.appendChild(zeichneLage(defs, l, 0, 0, kachelB, optionen(l, 0, kachelB))));
		}));
	}
	if (of && oh) g.appendChild(uebergangVerlauf(defs, 0, 0, b, of, oh, p));
	if (ol && unterStreu) g.appendChild(auslaufVerlauf(defs, 0, 0, b, unterStreu, ol, p));
	if ((of && oh) || (ol && unterStreu)) {
		g.appendChild(muster('grenzen', kachelB, 0, bildHoehe, ziel => {
			if (of && oh) uebergangKruemel(ziel, 0, 0, kachelB, of, oh, 0, dichte);
			if (ol && unterStreu) auslaufKruemel(ziel, 0, 0, kachelB, unterStreu, ol, 0, dichte);
		}));
	}
	if (ol) streuInReihen(defs, g, ol, b, faktor, bibliothek, p, r);
	return g;
}

type Zustand = 'ganz' | 'rest' | 'skelett';

/** Ein Blatt der Streu ist 30 Lupen-Pixel gross; dafuer sind die Striche der Formen gerechnet. */
const BLATT_MASS = 30;

/**
 * Die Blattformen fuer die Kacheln des Grubenbildes: Jede Form (Art, Zustand,
 * Farbe) liegt EINMAL in `defs`, jedes gelegte Blatt ist ein `<use>` mit
 * seiner Lage - ein Element statt fuenf. In der Lupe wird jedes Blatt
 * gezeichnet (`blatt`): Dort sind es wenige, und der Schatten faellt so immer
 * nach rechts unten, waehrend er sich hier mit dem Blatt dreht - bei Blaettern
 * von wenigen Pixeln nicht zu sehen.
 *
 * Die Striche sind gewoehnliche Striche in Blatt-Einheiten, die mit dem Bild
 * schrumpfen. Die Lupe haelt ihre Striche in Bildschirm-Pixeln
 * (`non-scaling-stroke`); in einer verkleinerten Gruppe blieben sie so dick
 * wie dort und erdrueckten ein Blatt von vier Pixeln.
 */
class BlattBibliothek {
	private readonly ids = new Map<string, string>();

	constructor(private readonly defs: SVGElement, private readonly idPraefix: string) {}

	/** Die id der Blattform; beim ersten Mal wird sie angelegt. */
	form(art: Blattart, zustand: Zustand, farbe: string): string {
		return this.hole(`${art}|${zustand}|${farbe}`, 'blatt', id => blattForm(id, art, zustand, farbe));
	}

	/** Ein Nadelpaar in dieser Farbe, `NADEL_MASS` lang. */
	nadel(farbe: string): string {
		return this.hole(`nadel|${farbe}`, 'nadel', id => nadelForm(id, farbe));
	}

	/** Ein Zweigstueck, `ZWEIG_MASS` lang. */
	zweig(): string {
		return this.hole('zweig', 'zweig', id => zweigForm(id));
	}

	private hole(schluessel: string, name: string, bauen: (id: string) => SVGElement): string {
		let id = this.ids.get(schluessel);
		if (!id) {
			id = `${this.idPraefix}-${name}${this.ids.size}`;
			this.ids.set(schluessel, id);
			this.defs.appendChild(bauen(id));
		}
		return id;
	}
}

/** Laenge der Nadeln und Zweige in der Bibliothek; ein gelegtes Stueck wird auf seine Laenge skaliert. */
const NADEL_MASS = 24;
const ZWEIG_MASS = 42;

/** Zwei Kiefernnadeln aus einer gemeinsamen Scheide, wie `nadelpaar` sie zeichnet. */
function nadelForm(id: string, farbe: string): SVGElement {
	const lang = NADEL_MASS;
	return el('g', { id, fill: 'none', stroke: farbe, 'stroke-width': 1.3, 'stroke-linecap': 'round' }, [
		el('path', { d: `M0 0 Q${f(lang * 0.5)} -1.2 ${f(lang)} -2.6` }),
		el('path', { d: `M0 0 Q${f(lang * 0.5)} 1.4 ${f(lang * 0.96)} 3.2` }),
		el('line', { x1: -1.5, y1: 0, x2: 1.5, y2: 0, stroke: '#4a3520', 'stroke-width': 2.2 }),
	]);
}

/** Ein Zweigstueck mit kurzem Seitentrieb, wie `zweig` es zeichnet. */
function zweigForm(id: string): SVGElement {
	const lang = ZWEIG_MASS;
	return el('g', { id, fill: 'none', stroke: '#6a4a2b', 'stroke-linecap': 'round' }, [
		el('path', { d: `M0 0 Q${f(lang * 0.5)} -2 ${f(lang)} 1`, 'stroke-width': 2.4 }),
		el('path', { d: `M${f(lang * 0.55)} -1 L${f(lang * 0.78)} -6`, 'stroke-width': 1.4 }),
	]);
}

/** Eine Blattform in Blatt-Einheiten, wie `blatt` sie zeichnet - ohne die zufaelligen Flecken. */
function blattForm(id: string, art: Blattart, zustand: Zustand, farbe: string): SVGElement {
	const form = blattform(art);
	const strich = { fill: 'none', 'stroke-linecap': 'round' };
	const breite = (px: number) => (px / BLATT_MASS).toFixed(4);
	const g = el('g', { id, transform: 'translate(0 0.5)' });
	if (zustand === 'skelett') {
		g.appendChild(el('path', {
			d: form.umriss + form.adern, ...strich, stroke: farbe, 'stroke-width': breite(0.7), opacity: 0.9,
		}));
		return g;
	}
	g.appendChild(el('path', {
		d: form.umriss, fill: '#1e140b', opacity: zustand === 'ganz' ? 0.3 : 0.4,
		transform: `translate(${breite(1)} ${breite(1.4)})`,
	}));
	if (zustand === 'ganz') g.appendChild(el('path', { d: form.stiel, ...strich, stroke: '#5a3d22', 'stroke-width': breite(1) }));
	g.appendChild(el('path', {
		d: form.umriss, fill: farbe, stroke: 'rgba(45,30,15,.65)', 'stroke-width': breite(0.7), 'stroke-linejoin': 'round',
	}));
	g.appendChild(el('path', { d: form.adern, ...strich, stroke: 'rgba(60,38,18,.55)', 'stroke-width': breite(0.55) }));
	return g;
}

/**
 * Die Streu des Grubenbildes: Reihe fuer Reihe, jede mit eigenem Zufall und
 * eigener Verschiebung.
 *
 * Der erste Wurf legte EIN Bild als Muster, der zweite ganze Kacheln ueber
 * die volle Hoehe in zufaelliger Folge - sechs verschiedene Spalten bleiben
 * sechs Spalten, und die erkannte das Auge (Nutzer-Befund 25.09.2026: „ein
 * Kindergartenbild nebeneinander kopiert"). Jetzt bekommt JEDE Blattreihe ihr
 * eigenes Muster: eine Kachel mit ihren Blaettern (Raster und Zufall wie in
 * der Lupe, `streuZelle`), als `<pattern>` ueber die Wandbreite gelegt und um
 * einen zufaelligen Betrag verschoben. Die Reihen ueberlappen sich wie in der
 * Lupe und werden in gemischter Reihenfolge gezeichnet. So gibt es keine zwei
 * gleichen Stellen uebereinander; nur innerhalb einer Reihe kehrt ihre Kachel
 * wieder, und die ist so breit, wie das Blattbudget hergibt
 * (`streuBlattBudget`) - bei grossen Blaettern meist breiter als die Wand.
 *
 * Kosten: hoechstens das Budget an Blaettern plus Nadeln und Zweige, je
 * Reihe ein Muster. Das Muster selbst kostet beim Zeichnen nichts - es wird
 * einmal gerastert und wiederholt.
 */
function streuInReihen(
	defs: SVGElement, g: SVGElement, ol: LupenLage, b: number, faktor: number,
	bibliothek: BlattBibliothek, idPraefix: string, r: () => number,
): void {
	const y = ol.von, h = ol.bis - ol.von;
	const masse = streuMasse(h);
	const { spalteB, reihen } = streuRaster(h);
	const zelleH = h / reihen;
	const grenzen = streuGrenzen(y, h, masse.ausdehnung);
	// Die Kachel einer Reihe: so viele Blaetter, wie das Budget je Reihe
	// hergibt, aber nie breiter als die Wand. Raster wie in der Lupe.
	const kachelB = Math.min(b, (streuBlattBudget(BLATT_MASS * faktor) / reihen) * spalteB);
	const spalten = Math.max(3, Math.ceil(kachelB / spalteB));
	const zelleB = kachelB / spalten;
	// Nadeln und Zweige je Kachel, in der Dichte der Lupe.
	const nadeln = Math.max(1, Math.round((kachelB * zelleH) / 900));
	const zweige = Math.round((kachelB * zelleH) / 3500);
	// So weit ragt ein Blatt oder eine Nadel ueber die Blattmitten seiner
	// Reihe hinaus - so hoch ist die Flaeche der Reihe.
	const ueberstand = masse.ausdehnung + NADEL_UEBERSTAND;

	// Die Reihen in gemischter Reihenfolge, sonst laege jede untere Reihe
	// ueber der oberen wie Dachziegel.
	mische(Array.from({ length: reihen }, (_, i) => i), r).forEach(re => {
		const rr = streu(Math.floor(r() * 0x7fffffff) + 1);
		const von = klemmeMitte(y + (re + 0.25) * zelleH, grenzen) - ueberstand;
		const bis = klemmeMitte(y + (re + 0.75) * zelleH, grenzen) + ueberstand;
		const id = `${idPraefix}-reihe${re}`;
		// Die Kachel in den Koordinaten ihres Musters: Dessen Ursprung liegt
		// oben links an der Flaeche der Reihe.
		const inhalt = el('g', { id, transform: `translate(0 ${f(-von)})` });
		// Zweige zuerst: Sie liegen unten, die Blaetter decken sie teilweise zu.
		for (let i = 0; i < zweige; i++) {
			zweig(inhalt, -10 + rr() * kachelB, von + ueberstand + rr() * (bis - von - 2 * ueberstand),
				30 + rr() * 24, -10 + rr() * 20, bibliothek);
		}
		const zellen = mische(Array.from({ length: spalten }, (_, i) => i), rr);
		const zuNadel = new Set(mische([...zellen], rr).slice(0, nadeln));
		zellen.forEach(sp => streuZelle(inhalt, sp, re, 0, y, zelleB, zelleH, masse, grenzen, zuNadel.has(sp), rr, bibliothek));
		defs.appendChild(inhalt);
		// Das Muster: die Kachel dreimal, versetzt um je eine Kachelbreite
		// (nichts ist an der Naht abgeschnitten), zufaellig verschoben. Einen
		// Pixel hoeher als die Flaeche, damit keine zweite Reihe Kacheln
		// hervorschaut.
		defs.appendChild(el('pattern', {
			id: `${id}-muster`, patternUnits: 'userSpaceOnUse',
			x: f(-rr() * kachelB), y: f(von), width: f(kachelB), height: f(bis - von + 1),
		}, [-kachelB, 0, kachelB].map(dx => el('use', { href: `#${id}`, x: f(dx) }))));
		g.appendChild(el('rect', {
			x: 0, y: f(von), width: f(b), height: f(bis - von),
			fill: `url(#${id}-muster)`, class: 'gb-auflage-reihe',
		}));
	});
}

/**
 * So weit reicht ein Nadelpaar ueber die Blattmitten seiner Reihe hinaus, in
 * Lupen-Pixeln: bis 29 lang, bis 30 Grad gedreht, eine halbe Zelle versetzt.
 */
const NADEL_UEBERSTAND = 18;

/**
 * Wohin die Blattmitten der Streu duerfen: Kein Blatt ragt oben ueber den
 * Rand hinaus, unten nur ein Stueck ueber die Grenze zum Of. Passt kein Blatt
 * in die Lage, stehen alle auf ihrer Mitte.
 */
interface StreuGrenzen { oberste: number; unterste: number; mitte: number }

function streuGrenzen(y: number, h: number, ausdehnung: number): StreuGrenzen {
	return { oberste: y + ausdehnung - UEBERSTAND, unterste: y + h - ausdehnung + UEBERSTAND_UNTEN, mitte: y + h / 2 };
}

function klemmeMitte(roh: number, gr: StreuGrenzen): number {
	return gr.oberste > gr.unterste ? gr.mitte : Math.max(gr.oberste, Math.min(gr.unterste, roh));
}

/**
 * Ein Blatt der Streu in seiner Rasterzelle (`sp`, `re`), etwas verrueckt, mit
 * Art, Farbe, Groesse und Drehung aus dem Zufall `r`; dazu, wenn `mitNadel`,
 * ein Nadelpaar daneben. Lupe (`foerna`) und Grubenbild (`streuInReihen`)
 * zeichnen ihre Zellen hiermit - dieselben Blaetter, dasselbe Raster.
 */
function streuZelle(
	g: SVGElement, sp: number, re: number, x: number, y: number, zelleB: number, zelleH: number,
	masse: { stauchen: number; groesse: number }, grenzen: StreuGrenzen, mitNadel: boolean,
	r: () => number, bibliothek: BlattBibliothek | null,
): void {
	const cx = x + (sp + 0.5 + (r() - 0.5) * 0.5) * zelleB;
	const cy = klemmeMitte(y + (re + 0.5 + (r() - 0.5) * 0.5) * zelleH, grenzen);
	const blattart = BLATTARTEN_STREU[Math.floor(r() * BLATTARTEN_STREU.length)];
	const farbe = r() < 0.2
		? GEBLEICHT[Math.floor(r() * GEBLEICHT.length)]
		: BLATT_FARBEN[blattart][Math.floor(r() * 3)];
	// Gedreht nur so weit, dass der Stiel nach unten weist - nach oben
	// zeigend ragte er ueber den Rand.
	blatt(g, blattart, cx, cy, (blattart === 'ahorn' ? 1.1 : 1) * masse.groesse * (0.9 + r() * 0.25),
		-80 + r() * 160, masse.stauchen, farbe, r, 'ganz', bibliothek);
	// Nadeln liegen zwischen und auf den Blaettern, nicht an deren Stelle.
	if (mitNadel) {
		nadelpaar(g, cx + (r() - 0.5) * zelleB, cy + (r() - 0.5) * zelleH, 20 + r() * 9,
			-30 + r() * 60, NADEL_FARBEN[Math.floor(r() * NADEL_FARBEN.length)], bibliothek);
	}
}

/** Zahl mit drei Nachkommastellen - fuer die verkleinerte Gruppe reicht eine nicht. */
const f3 = (v: number) => v.toFixed(3);

/**
 * Die Lage unter der Streu (meist Of) laeuft nach oben in die Luecken der
 * Streu aus, statt mit einer geraden Kante aufzuhoeren (Nutzer-Wunsch
 * 21.09.2026: „bisschen weicher auch von Of nach Ol").
 *
 * Gezeichnet VOR der Streu: Ein Verlauf in der Farbe der Lage, der nach oben
 * durchsichtig wird, und einzelne Kruemel und Blattreste, nach oben immer
 * seltener. Man sieht beides nur in den Luecken zwischen den Blaettern - so
 * wie im Anschnitt, wo die Streu locker auf dem Of liegt.
 */
function auslaufen(
	defs: SVGElement, x: number, oben: number, b: number,
	unter: LupenLage, streuLage: LupenLage, idPraefix: string,
): SVGElement {
	const g = el('g', { class: 'lupe-auslaufen' });
	g.appendChild(auslaufVerlauf(defs, x, oben, b, unter, streuLage, idPraefix));
	auslaufKruemel(g, x, oben, b, unter, streuLage, 0);
	return g;
}

/** Wie weit die Lage unter der Streu nach oben auslaeuft, in Pixel. */
function auslaufTiefe(unter: LupenLage, streuLage: LupenLage): number {
	return Math.max(3, Math.min(12, 0.6 * (streuLage.bis - streuLage.von), 0.4 * (unter.bis - unter.von)));
}

/** Der Verlauf des Auslaufens: die Farbe der Lage, nach oben durchsichtig. */
function auslaufVerlauf(
	defs: SVGElement, x: number, oben: number, b: number,
	unter: LupenLage, streuLage: LupenLage, idPraefix: string,
): SVGElement {
	const grenze = oben + unter.von;
	const d = auslaufTiefe(unter, streuLage);
	const farbe = HORIZONT_FARBEN[unter.feld].farbe;
	const id = `${idPraefix}-auslaufen`;
	defs.appendChild(el('linearGradient', { id, x1: 0, y1: 0, x2: 0, y2: 1 }, [
		el('stop', { offset: '0%', 'stop-color': farbe, 'stop-opacity': 0 }),
		el('stop', { offset: '100%', 'stop-color': farbe, 'stop-opacity': 0.85 }),
	]));
	return el('rect', { x, y: grenze - d, width: b, height: d, fill: `url(#${id})` });
}

/** Die Kruemel und Blattreste des Auslaufens, nach oben immer seltener. */
function auslaufKruemel(
	g: SVGElement, x: number, oben: number, b: number,
	unter: LupenLage, streuLage: LupenLage, variante: number, dichte = 1,
): void {
	const grenze = oben + unter.von;
	const d = auslaufTiefe(unter, streuLage);
	const r = streu(5309 + variante * VARIANTEN_SCHRITT);
	const n = Math.round((b * d) / 16 * dichte);
	const grob = 1 / Math.sqrt(dichte);
	const farben = unter.feld === 'Of' ? [...MASSE_FARBEN, ...REST_FARBEN] : KRUEMEL_FARBEN;
	for (let i = 0; i < n; i++) {
		const rad = (0.9 + r() * 1.4) * grob;
		g.appendChild(el('ellipse', {
			cx: f(x + rad + r() * (b - 2 * rad)), cy: f(grenze - d * r() * r()),
			rx: f(rad * 1.4), ry: f(rad * 0.75),
			fill: farben[Math.floor(r() * farben.length)], opacity: 0.9,
		}));
	}
}

/**
 * Der Uebergang zwischen Of und Oh: fliessend statt als Kante (Nutzer-Wunsch
 * 21.09.2026 - im Gelaende geht die Vermoderung allmaehlich in den Feinhumus
 * ueber; SEA: „der Feinsubstanzanteil nimmt innerhalb des Of-Horizontes von
 * oben nach unten zu").
 *
 * Zwei Dinge liegen ueber der Grenze: ein Verlauf, der das untere Of dunkler
 * und das obere Oh heller macht, und Kruemel der einen Lage, die in die
 * andere hineinreichen - nach aussen immer seltener. Wie weit, richtet sich
 * nach der duenneren der beiden Lagen, damit keine im Uebergang verschwindet.
 * An den Maechtigkeiten aendert das nichts: Die Grenze liegt, wo sie liegt.
 */
function uebergang(
	defs: SVGElement, x: number, oben: number, b: number,
	of: LupenLage, oh: LupenLage, idPraefix: string,
): SVGElement {
	const g = el('g', { class: 'lupe-uebergang' });
	g.appendChild(uebergangVerlauf(defs, x, oben, b, of, oh, idPraefix));
	uebergangKruemel(g, x, oben, b, of, oh, 0);
	return g;
}

/** Wie weit der Uebergang nach oben und unten reicht, in Pixel. */
function uebergangTiefe(of: LupenLage, oh: LupenLage): number {
	return Math.max(3, Math.min(14, 0.4 * Math.min(of.bis - of.von, oh.bis - oh.von)));
}

/** Der Verlauf des Uebergangs: unteres Of dunkler, oberes Oh heller. */
function uebergangVerlauf(
	defs: SVGElement, x: number, oben: number, b: number,
	of: LupenLage, oh: LupenLage, idPraefix: string,
): SVGElement {
	const grenze = oben + oh.von;
	const d = uebergangTiefe(of, oh);
	const ofFarbe = HORIZONT_FARBEN['Of'].farbe, ohFarbe = HORIZONT_FARBEN['Oh'].farbe;
	const id = `${idPraefix}-verlauf`;
	defs.appendChild(el('linearGradient', { id, x1: 0, y1: 0, x2: 0, y2: 1 }, [
		el('stop', { offset: '0%', 'stop-color': ohFarbe, 'stop-opacity': 0 }),
		el('stop', { offset: '46%', 'stop-color': ohFarbe, 'stop-opacity': 0.55 }),
		el('stop', { offset: '54%', 'stop-color': ofFarbe, 'stop-opacity': 0.45 }),
		el('stop', { offset: '100%', 'stop-color': ofFarbe, 'stop-opacity': 0 }),
	]));
	return el('rect', { x, y: grenze - d, width: b, height: 2 * d, fill: `url(#${id})` });
}

/**
 * Die Kruemel des Uebergangs, ueber die Grenze hinweg und nach aussen
 * seltener (das Quadrat des Zufalls haeuft sie nahe der Grenze).
 */
function uebergangKruemel(
	g: SVGElement, x: number, oben: number, b: number,
	of: LupenLage, oh: LupenLage, variante: number, dichte = 1,
): void {
	const grenze = oben + oh.von;
	const d = uebergangTiefe(of, oh);
	const r = streu(4201 + variante * VARIANTEN_SCHRITT);
	const n = Math.round((b * d) / 14 * dichte);
	const grob = 1 / Math.sqrt(dichte);
	for (let i = 0; i < n; i++) {
		const nachOben = r() < 0.5;
		const abstand = d * r() * r();
		const rad = (0.9 + r() * 1.2) * grob;
		g.appendChild(el('ellipse', {
			cx: f(x + rad + r() * (b - 2 * rad)), cy: f(nachOben ? grenze - abstand : grenze + abstand),
			rx: f(rad * 1.2), ry: f(rad * 0.8),
			fill: nachOben
				? KRUEMEL_FARBEN[Math.floor(r() * KRUEMEL_FARBEN.length)]
				: MASSE_FARBEN[2 + Math.floor(r() * 2)],
			opacity: 0.85,
		}));
	}
}

/**
 * Kuerzel und Maechtigkeit, darunter Name und Erlaeuterung. Steht die
 * Beschriftung nicht auf Hoehe ihrer Lage, fuehrt eine Linie zu ihr.
 */
function beschrifte(svg: SVGElement, t: LupenText, oben: number): void {
	const kante = RAND + LUPE_BILD_BREITE;
	const tx = kante + TEXT_ABSTAND;
	const text = LUPEN_TEXT[t.lage.feld];
	const kopfY = oben + t.oben + 10;
	const mitteLage = oben + (t.lage.von + t.lage.bis) / 2;
	svg.appendChild(el('path', {
		d: `M${f(kante)} ${f(mitteLage)} L${f(kante + 5)} ${f(mitteLage)} L${f(tx - 3)} ${f(kopfY - 4)}`,
		fill: 'none', stroke: '#a49c90', 'stroke-width': 0.8, class: 'lupe-fuehrung',
	}));
	svg.appendChild(schrift(tx, kopfY, t.lage.feld, { 'font-size': 13, 'font-weight': 700 }));
	svg.appendChild(schrift(tx + 24, kopfY, `${String(t.lage.maechtigkeit).replace('.', ',')} cm`, { 'font-size': 11.5 }));
	let ty = kopfY + ZEILE;
	svg.appendChild(schrift(tx, ty, text.name, { 'font-size': 10.5, 'font-weight': 600, fill: '#3d3730' }));
	umbrechen(text.erlaeuterung).forEach(z => {
		ty += ZEILE;
		svg.appendChild(schrift(tx, ty, z, { 'font-size': 10.5, fill: '#5f5850' }));
	});
}

function schrift(x: number, y: number, inhalt: string, attr: Record<string, string | number>): SVGElement {
	const t = el('text', { x, y, fill: '#2b2722', ...attr });
	t.textContent = inhalt;
	return t;
}

/** Mischt eine Liste mit dem Zufall des Bildes (dasselbe Bild sieht immer gleich aus). */
function mische<T>(liste: T[], r: () => number): T[] {
	for (let i = liste.length - 1; i > 0; i--) {
		const j = Math.floor(r() * (i + 1));
		[liste[i], liste[j]] = [liste[j], liste[i]];
	}
	return liste;
}

/** Zahl mit einer Nachkommastelle - haelt die Pfadangaben kurz. */
const f = (v: number) => v.toFixed(1);

/** Eine leicht gewellte Linie von links nach rechts (Wurzel, Pilzfaden). */
function schlinge(x1: number, y1: number, x2: number, y2: number, hub: number, r: () => number): string {
	const mx = (x1 + x2) / 2 + (r() - 0.5) * hub, my = (y1 + y2) / 2 + (r() - 0.5) * hub;
	return `M${f(x1)} ${f(y1)} Q${f(mx)} ${f(my)} ${f(x2)} ${f(y2)}`;
}

/* --- Ol: Blaetter, Nadeln, ein Zweigstueck -------------------------------- */

/**
 * Herbstfarben je Art - Ahorn gelb bis orangebraun, Aspe gelb bis
 * schwarzbraun - und fuer jede Art ein ausgebleichtes Grau (SEA: Bleichung
 * und Vergrauung beginnen schon in der Foerna).
 */
const BLATT_FARBEN: Record<Blattart, string[]> = {
	ahorn: ['#c98b3a', '#b8742e', '#a8662f'],
	linde: ['#9c6a36', '#8a5a2e', '#b08050'],
	ulme: ['#8e6a3c', '#7d5b33', '#a07a48'],
	aspe: ['#bf9f55', '#6e5a3e', '#a78a4a'],
};
const GEBLEICHT = ['#a39987', '#8f8676'];
const NADEL_FARBEN = ['#7d5a33', '#957143', '#6f6453'];

/**
 * Die Streu liegt locker: ganze Blaetter, flach liegend und deshalb schraeg
 * gesehen gestaucht, eines halb ueber dem anderen. Dazwischen Kiefernnadeln
 * (paarweise) und Zweigstuecke.
 *
 * Grosse Blaetter (Nutzer-Wunsch 21.09.2026), und KEIN Grund darunter: Die
 * Blaetter fuellen die Lage, die Luecken bleiben durchsichtig.
 *
 * Verteilt wird ueber ein Raster, in dem jedes Blatt seinen Platz bekommt und
 * darin nur etwas verrueckt wird (Nutzer-Befund 21.09.2026: „weniger
 * ueberlappen, mehr Blaetter, zu viele Luecken"). Rein zufaellig gestreut
 * haeuften sich Blaetter an einer Stelle, waehrend daneben Loecher blieben.
 * Ueber die Grenzen der Lage ragen sie nur wenig (`UEBERSTAND`).
 */
function foerna(g: SVGElement, x: number, y: number, b: number, h: number, r: () => number): void {
	const masse = streuMasse(h);
	// Ein Raster knapp enger als die Blaetter breit und hoch sind: Sie
	// beruehren und decken sich an den Raendern, liegen aber nicht aufeinander.
	const { spalteB, reihen } = streuRaster(h);
	const spalten = Math.max(3, Math.ceil(b / spalteB));
	const zelleB = b / spalten, zelleH = h / reihen;

	// Zweige zuerst: Sie liegen unten, die Blaetter decken sie teilweise zu.
	const zweige = Math.max(1, Math.round((b * h) / 3500));
	for (let i = 0; i < zweige; i++) {
		zweig(g, x - 10 + r() * b, y + h * (0.2 + r() * 0.6), 30 + r() * 24, -10 + r() * 20);
	}

	// Je Rasterzelle ein Blatt, in gemischter Reihenfolge gezeichnet - sonst
	// laege jedes untere Blatt ueber dem oberen wie Dachziegel.
	const zellen = mische(
		Array.from({ length: spalten * reihen }, (_, i) => [i % spalten, Math.floor(i / spalten)] as const), r);
	const nadeln = Math.max(3, Math.round((b * h) / 900));
	const zuNadel = new Set(mische(zellen.map((_, i) => i), r).slice(0, nadeln));
	const grenzen = streuGrenzen(y, h, masse.ausdehnung);
	zellen.forEach(([sp, re], i) => streuZelle(g, sp, re, x, y, zelleB, zelleH, masse, grenzen, zuNadel.has(i), r, null));
}

/**
 * So weit duerfen Blaetter der Streu ueber die Grenzen ihrer Lage
 * hinausragen, in Pixel: oben wenig, unten etwas mehr - dort liegen sie auf
 * dem Of auf (Nutzer-Wunsch 21.09.2026: „kann auch ein bisschen mehr
 * ueberlappen").
 */
const UEBERSTAND = 4;
const UEBERSTAND_UNTEN = 8;

/**
 * Groesse und Stauchung der Streublaetter bei einer Lage von `h` Pixel, und
 * wie weit ein Blatt hoechstens ueber und unter seine Mitte reicht.
 *
 * Das Blatt dreht um die Mitte seiner Spreite; die ist in der Einheit der
 * Blattformen hoechstens 0,58 breit (Ahorn) und 0,5 lang. Das groesste Blatt
 * ist 1,1 (Ahorn) mal 1,15 (Streuung) so gross wie `groesse`.
 */
function streuMasse(h: number): { stauchen: number; groesse: number; ausdehnung: number } {
	const stauchen = Math.max(0.45, Math.min(0.7, h / 60));
	const groesse = 30;
	return { stauchen, groesse, ausdehnung: 0.6 * groesse * 1.1 * 1.15 * stauchen };
}

/**
 * Das Raster der Streu bei einer Lage von `h` Pixel: die Breite einer
 * Blattspalte und die Zahl der Reihen. Daraus rechnet `foerna` seine Zellen
 * und `legeAuflageAusKacheln` die Breite einer Streu-Kachel.
 */
function streuRaster(h: number): { spalteB: number; reihen: number } {
	const { stauchen, groesse } = streuMasse(h);
	return { spalteB: groesse * 0.62, reihen: Math.max(1, Math.round(h / (groesse * stauchen * 0.55))) };
}

/** Wie weit die Streu ueber die Oberkante ihrer Lage hinausreicht, in Pixel. */
export function streuUeberstandOben(h: number): number {
	const { ausdehnung } = streuMasse(h);
	// Passt kein Blatt in die Lage, stehen alle auf ihrer Mitte.
	return ausdehnung - UEBERSTAND > h - ausdehnung + UEBERSTAND_UNTEN
		? Math.max(0, ausdehnung - h / 2)
		: UEBERSTAND;
}

/**
 * Ein Blatt einer Art, beliebig gedreht, flach liegend: Die Stauchung wirkt
 * senkrecht im Bild, nicht entlang des Blattes - so liegt es in der Ebene.
 * Darunter ein weicher Schatten; manche tragen die dunklen Flecken, die die
 * SEA fuer die Foerna beschreibt (Punktierung, Fleckung).
 */
function blatt(
	g: SVGElement, art: Blattart, cx: number, cy: number, groesse: number,
	dreh: number, stauchen: number, farbe: string, r: () => number,
	zustand: Zustand = 'ganz', bibliothek: BlattBibliothek | null = null,
): void {
	const spiegel = r() < 0.5 ? -1 : 1;
	const lage = (dx: number, dy: number) =>
		`translate(${f(cx + dx)} ${f(cy + dy)}) scale(1 ${stauchen.toFixed(2)}) rotate(${dreh.toFixed(0)}) `
		+ `scale(${f(groesse * spiegel)} ${f(groesse)})`;
	const klasse = `lupe-blatt lupe-${art} lupe-${zustand}`;
	// In den Kacheln des Grubenbildes liegt die Form in defs; hier nur ihre Lage.
	if (bibliothek) {
		g.appendChild(el('use', { href: `#${bibliothek.form(art, zustand, farbe)}`, transform: lage(0, 0), class: klasse }));
		return;
	}
	const form = blattform(art);
	const strich = { fill: 'none', 'vector-effect': 'non-scaling-stroke', 'stroke-linecap': 'round' };
	const gruppe = el('g', { transform: `${lage(0, 0)} translate(0 0.5)`, class: klasse });
	if (zustand === 'skelett') {
		// Nur noch das Adernetz: Die Blattflaechen zwischen den Adern sind
		// weggefressen (SEA: Skelettierung).
		gruppe.appendChild(el('path', {
			d: form.umriss + form.adern, ...strich, stroke: farbe, 'stroke-width': 0.7, opacity: 0.9,
		}));
		g.appendChild(gruppe);
		return;
	}
	g.appendChild(el('path', {
		d: form.umriss, fill: '#1e140b', opacity: zustand === 'ganz' ? 0.3 : 0.4, transform: `${lage(1, 1.4)} translate(0 0.5)`,
	}));
	if (zustand === 'ganz') gruppe.appendChild(el('path', { d: form.stiel, ...strich, stroke: '#5a3d22', 'stroke-width': 1 }));
	gruppe.appendChild(el('path', {
		d: form.umriss, fill: farbe, stroke: 'rgba(45,30,15,.65)', 'stroke-width': 0.7,
		'stroke-linejoin': 'round', 'vector-effect': 'non-scaling-stroke',
	}));
	gruppe.appendChild(el('path', { d: form.adern, ...strich, stroke: 'rgba(60,38,18,.55)', 'stroke-width': 0.55 }));
	if (zustand === 'ganz' && r() < 0.4) {
		const flecken = 1 + Math.floor(r() * 3);
		for (let k = 0; k < flecken; k++) {
			gruppe.appendChild(el('ellipse', {
				cx: ((r() - 0.5) * 0.3).toFixed(3), cy: (-0.3 - r() * 0.4).toFixed(3),
				rx: (0.025 + r() * 0.03).toFixed(3), ry: (0.02 + r() * 0.025).toFixed(3),
				fill: '#3a2614', opacity: 0.5,
			}));
		}
	}
	g.appendChild(gruppe);
}

/** Zwei Kiefernnadeln aus einer gemeinsamen Scheide. */
function nadelpaar(
	g: SVGElement, cx: number, cy: number, lang: number, dreh: number, farbe: string,
	bibliothek: BlattBibliothek | null = null,
): void {
	// In den Kacheln des Grubenbildes liegt die Form in defs; hier nur ihre Lage.
	if (bibliothek) {
		g.appendChild(el('use', {
			href: `#${bibliothek.nadel(farbe)}`, class: 'lupe-nadel',
			transform: `translate(${f(cx)} ${f(cy)}) rotate(${dreh.toFixed(0)}) scale(${(lang / NADEL_MASS).toFixed(3)})`,
		}));
		return;
	}
	const form = el('g', {
		transform: `translate(${f(cx)} ${f(cy)}) rotate(${dreh.toFixed(0)})`,
		fill: 'none', stroke: farbe, 'stroke-width': 1.3, 'stroke-linecap': 'round', class: 'lupe-nadel',
	});
	form.appendChild(el('path', { d: `M0 0 Q${f(lang * 0.5)} -1.2 ${f(lang)} -2.6` }));
	form.appendChild(el('path', { d: `M0 0 Q${f(lang * 0.5)} 1.4 ${f(lang * 0.96)} 3.2` }));
	form.appendChild(el('line', { x1: -1.5, y1: 0, x2: 1.5, y2: 0, stroke: '#4a3520', 'stroke-width': 2.2 }));
	g.appendChild(form);
}

/** Ein Zweigstueck mit kurzem Seitentrieb. */
function zweig(
	g: SVGElement, cx: number, cy: number, lang: number, dreh: number,
	bibliothek: BlattBibliothek | null = null,
): void {
	if (bibliothek) {
		g.appendChild(el('use', {
			href: `#${bibliothek.zweig()}`, class: 'lupe-zweig',
			transform: `translate(${f(cx)} ${f(cy)}) rotate(${dreh.toFixed(0)}) scale(${(lang / ZWEIG_MASS).toFixed(3)})`,
		}));
		return;
	}
	const form = el('g', {
		transform: `translate(${f(cx)} ${f(cy)}) rotate(${dreh.toFixed(0)})`,
		fill: 'none', stroke: '#6a4a2b', 'stroke-linecap': 'round',
	});
	form.appendChild(el('path', { d: `M0 0 Q${f(lang * 0.5)} -2 ${f(lang)} 1`, 'stroke-width': 2.4 }));
	form.appendChild(el('path', { d: `M${f(lang * 0.55)} -1 L${f(lang * 0.78)} -6`, 'stroke-width': 1.4 }));
	g.appendChild(form);
}

/* --- Of: Blattreste, Pilzfaeden, Feinwurzeln, Kotkruemel ------------------ */

/** Angerottete Blattreste: dunkler als die Streu, einige gebleicht (SEA). */
const REST_FARBEN = ['#6b4526', '#7a5431', '#5c3d22', '#86603a', '#8e826c', '#9d927e'];
/** Die kruemelige Masse, in der die Reste stecken. */
const MASSE_FARBEN = ['#3e2a18', '#4a331d', '#5a3f24', '#71512f'];

/**
 * Die Vermoderungslage nach dem Vorbild eines echten Anschnitts (Foto des
 * Nutzers, 21.09.2026): Blattstuecke und Zweigreste liegen waagerecht in
 * einer dunkelbraunen, kruemeligen Masse, halb von ihr verschuettet.
 *
 *  - Blattreste: kleine Blaetter derselben Arten wie in der Streu, stark
 *    gestaucht und angerottet; manche nur noch als Adernetz (Skelettierung),
 *  - Nadel- und Zweigstuecke, kurz und fast waagerecht,
 *  - Kruemel ueber allem, damit die Reste darin stecken statt darauf zu
 *    liegen,
 *  - weisse Pilzfaeden, aber NESTERWEISE (Verpilzung, SEA) - ueberall
 *    verteilt sahen sie aus wie Heu,
 *  - einige Feinwurzeln und Kotkruemel von Bodentieren.
 */
function vermoderung(
	g: SVGElement, x: number, y: number, b: number, h: number, r: () => number,
	bibliothek: BlattBibliothek | null = null, dichte = 1,
): void {
	const flaeche = b * h;
	// Weniger, dafuer groessere Kruemel in den Kacheln (`kruemelDichte`).
	const grob = 1 / Math.sqrt(dichte);
	const kruemel = (anzahl: number, groesse: number) => {
		for (let i = 0; i < Math.round(anzahl * dichte); i++) {
			const rad = groesse * grob * (0.6 + r() * 0.8);
			g.appendChild(el('ellipse', {
				cx: f(x + r() * b), cy: f(y + r() * h), rx: f(rad * 1.3), ry: f(rad * 0.8),
				fill: MASSE_FARBEN[Math.floor(r() * MASSE_FARBEN.length)], opacity: 0.85,
			}));
		}
	};
	kruemel(Math.round(flaeche / 18), 1.3);

	// Nadel- und Zweigstuecke.
	const stuecke = Math.round(flaeche / 420);
	for (let i = 0; i < stuecke; i++) {
		const cx = x + r() * b, cy = y + r() * h, lang = 6 + r() * 10, w = (-18 + r() * 36) * Math.PI / 180;
		g.appendChild(el('line', {
			x1: f(cx - Math.cos(w) * lang / 2), y1: f(cy - Math.sin(w) * lang / 2),
			x2: f(cx + Math.cos(w) * lang / 2), y2: f(cy + Math.sin(w) * lang / 2),
			stroke: ['#8a6a44', '#6e5236', '#9a8a70'][Math.floor(r() * 3)], 'stroke-width': f(1 + r() * 0.9),
			'stroke-linecap': 'round',
		}));
	}

	// Blattreste, waagerecht liegend.
	const reste = Math.max(4, Math.round(flaeche / 230));
	for (let i = 0; i < reste; i++) {
		const art = BLATTARTEN_STREU[Math.floor(r() * BLATTARTEN_STREU.length)];
		const skelett = r() < 0.25;
		const farbe = skelett ? '#c2a47a' : REST_FARBEN[Math.floor(r() * REST_FARBEN.length)];
		blatt(g, art, x + r() * b, y + r() * h, 13 + r() * 8, r() * 360,
			0.3 + r() * 0.15, farbe, r, skelett ? 'skelett' : 'rest', bibliothek);
	}

	// Kruemel darueber: Die Reste stecken in der Masse.
	kruemel(Math.round(flaeche / 55), 1.1);

	// Kotkruemel der Bodentiere, in kleinen Haeufchen.
	const haeufchen = Math.max(1, Math.round(Math.max(2, Math.round(flaeche / 1400)) * dichte));
	for (let i = 0; i < haeufchen; i++) {
		const hx = x + r() * b, hy = y + r() * h;
		const anzahl = 3 + Math.floor(r() * 4);
		for (let k = 0; k < anzahl; k++) {
			g.appendChild(el('ellipse', {
				cx: f(hx + (r() - 0.5) * 7 * grob), cy: f(hy + (r() - 0.5) * 4 * grob),
				rx: f((1 + r() * 0.6) * grob), ry: f((0.8 + r() * 0.4) * grob), fill: '#1e130a',
			}));
		}
	}

	// Feinwurzeln: wenige, lang, waagerecht durch die Lage.
	const wurzeln = Math.max(2, Math.round(h / 40));
	for (let i = 0; i < wurzeln; i++) {
		const y1 = y + r() * h, y2 = y1 + (r() - 0.5) * h * 0.4;
		g.appendChild(el('path', {
			d: schlinge(x - 2, y1, x + b + 2, y2, 16, r),
			fill: 'none', stroke: '#c9a472', 'stroke-width': 0.9, opacity: 0.85,
		}));
	}

	// Pilzfaeden in Nestern: um einen Punkt herum ein Geflecht feiner Faeden.
	const nester = Math.max(1, Math.round(flaeche / 5000));
	for (let i = 0; i < nester; i++) {
		const nx = x + (0.15 + r() * 0.7) * b, ny = y + r() * h;
		let d = '';
		const faeden = 10 + Math.floor(r() * 8);
		for (let k = 0; k < faeden; k++) {
			const w = r() * Math.PI * 2, lang = 5 + r() * 10;
			const x1 = nx + (r() - 0.5) * 12, y1 = ny + (r() - 0.5) * 6;
			d += schlinge(x1, y1, x1 + Math.cos(w) * lang, y1 + Math.sin(w) * lang * 0.45, 6, r) + ' ';
		}
		g.appendChild(el('path', {
			d: d.trim(), fill: 'none', stroke: '#f5f0e4', 'stroke-width': 0.45, opacity: 0.8,
			class: 'lupe-pilz',
		}));
	}
}

/** Die Arten der Streu, gewichtet - Ahorn und Linde haeufiger. */
const BLATTARTEN_STREU: Blattart[] = ['ahorn', 'ahorn', 'linde', 'linde', 'ulme', 'aspe', 'aspe'];

/* --- Oh: schwaerzlich, kruemelig, durchwurzelt ---------------------------- */

const KRUEMEL_FARBEN = ['#22170e', '#2e2016', '#3d2c1e', '#4a3726'];

/**
 * Dicht an dicht kleine, runde Kruemel - kaffeesatzartig, ohne erkennbare
 * Pflanzenreste. Ein Teil traegt oben links einen hellen Punkt, damit sie
 * nach Kruemel aussehen und nicht nach Rauschen. Dazu einige Feinwurzeln,
 * die von oben hereinwachsen (SEA: „mehr oder weniger stark durchwurzelt").
 */
function feinhumus(g: SVGElement, x: number, y: number, b: number, h: number, r: () => number, dichte = 1): void {
	g.appendChild(el('rect', { x, y, width: b, height: h, fill: '#1c130c', opacity: 0.3 }));
	// Weniger, dafuer groessere Kruemel in den Kacheln (`kruemelDichte`).
	const grob = 1 / Math.sqrt(dichte);
	const n = Math.round((b * h) / 11 * dichte);
	let lichter = '';
	for (let i = 0; i < n; i++) {
		const cx = x + r() * b, cy = y + r() * h, rad = (1.1 + r() * 1.4) * grob;
		g.appendChild(el('ellipse', {
			cx: f(cx), cy: f(cy), rx: f(rad), ry: f(rad * (0.75 + r() * 0.25)),
			fill: KRUEMEL_FARBEN[Math.floor(r() * KRUEMEL_FARBEN.length)],
		}));
		if (rad > 1.8 * grob && r() < 0.5) {
			lichter += `M${f(cx - rad * 0.35)} ${f(cy - rad * 0.3)}h0.01`;
		}
	}
	// Alle Lichtpunkte in EINEM Pfad (runde Enden machen aus der Mini-Linie einen Punkt).
	if (lichter) {
		g.appendChild(el('path', {
			d: lichter, stroke: '#7a6048', 'stroke-width': 1, 'stroke-linecap': 'round', opacity: 0.7,
		}));
	}
	const wurzeln = Math.max(2, Math.round(b / 50));
	for (let i = 0; i < wurzeln; i++) {
		const x1 = x + (i + 0.3 + r() * 0.4) * (b / wurzeln);
		g.appendChild(el('path', {
			d: schlinge(x1, y - 2, x1 + (r() - 0.5) * 30, y + h + 2, 14, r),
			fill: 'none', stroke: '#b08a5a', 'stroke-width': 0.9, opacity: 0.75,
		}));
	}
}
